// termofuse.jsx
import React from "react";
import TermOfUseLayout from "../../../components/TermOfUseLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function VeilifyPhotoVaultTerms() {
  return (
    <TermOfUseLayout
      title="terms of use"
      appName="Veilify Photo Vault"
      date="DECEMBER 2025"
    >
      <div className={s.title}>1) Acceptance of Terms</div>
      <p>
        These Terms of Use (the “Terms”) govern your access to and use of
        “Veilify Photo Vault” (the “App”). By downloading, installing,
        accessing, or using the App, you agree to be bound by these Terms. If
        you do not agree, you must uninstall the App and discontinue all use.
        Where required by platform rules or local law, additional terms may
        apply; in case of conflict with App Store or Google Play terms, the
        platform terms may prevail to the extent required.
      </p>

      <div className={s.title}>2) Description of the App</div>
      <p>
        The App provides a private photo and video vault on your device. It
        allows you to import media from your system gallery, store it in a
        protected area locked by a PIN code and/or biometric authentication, and
        organize it into albums. Files are intended to be stored locally on your
        device in encrypted form, and are not automatically backed up to our own
        servers. The App is designed to improve privacy and organization on your
        device but does not replace system-level security or backups.
      </p>

      <div className={s.title}>3) Eligibility</div>
      <p>
        You may use the App only if you have the legal capacity to enter into a
        binding agreement under the laws of your jurisdiction. Where parental
        consent is required by law, a parent or guardian must approve use. By
        using the App, you confirm that you meet applicable age and eligibility
        requirements or have obtained the necessary consent.
      </p>

      <div className={s.title}>4) Local Storage, Encryption, and Risks</div>
      <p>
        The App is designed so that photos and videos you import into the vault
        are stored locally on your device in an encrypted container. Encryption
        keys may be protected using platform mechanisms such as the
        Keychain/Keystore and biometric APIs where enabled. However, you
        acknowledge that:
      </p>
      <ul>
        <li>
          if you uninstall the App, reset your device, lose your PIN, lose
          access to biometrics, or change devices without proper export/backup,
          you may permanently lose access to your vault content;
        </li>
        <li>
          we cannot recover your PIN or decrypt your content for you if you lose
          your credentials;
        </li>
        <li>
          device-level security (OS lock screen, system backups, malware
          protection) remains your responsibility and may affect the real level
          of protection.
        </li>
      </ul>
      <p>
        You should keep your device OS updated, use a secure device passcode,
        and avoid sharing your PIN or biometric access with others.
      </p>

      <div className={s.title}>5) Import, Export, and System Gallery</div>
      <p>
        When you import items into the App, it may copy or move photos and
        videos from your system gallery into its private storage area. Depending
        on the mode you choose, originals in the system gallery may remain or be
        deleted; always verify your settings and results before deleting
        anything from the system Photos app. When you export or share items from
        the vault back to other apps or storage, those items may become visible
        again outside the vault and subject to the policies and security of
        other services.
      </p>
      <p>
        The App does not control system-level cloud backups (for example, iCloud
        or Google Photos). If you had previously synced media to the cloud
        before importing it into Veilify Photo Vault, copies may still exist in
        those services even if you later delete local versions.
      </p>

      <div className={s.title}>6) Free and Premium Versions</div>
      <p>
        The App may offer a free tier and a subscription tier. In the free
        version, functionality may be limited (for example, up to a fixed number
        of photos/videos, a limited number of albums, or restricted access to
        biometric unlock). The premium subscription may unlock higher or
        unlimited capacity, additional albums, biometric access, or other
        features described in the App or on the paywall screen. The specific
        limits and features may be updated over time.
      </p>

      <div className={s.title}>7) Subscriptions, Trials, and Billing</div>
      <p>
        Premium features may be available through auto-renewable subscriptions
        or other in-app purchases (“Subscriptions”), such as weekly and yearly
        plans, potentially with a free trial period. Exact pricing, durations,
        and offers may vary by region and may change over time as displayed on
        the paywall screen and in the app stores.
      </p>
      <p>
        Purchases are processed by Apple or Google via your store account; we do
        not receive your payment card numbers. If a free trial is offered, it
        will convert into a paid Subscription unless cancelled at least 24 hours
        before the end of the trial period in your account settings. You can
        manage or cancel Subscriptions directly in platform subscription
        settings. Deleting the App does not automatically cancel a Subscription.
        Refunds, grace periods, and other billing matters are determined by the
        policies of the relevant app store.
      </p>

      <div className={s.title}>8) Entitlements and Access Control</div>
      <p>
        Subscription status and eligibility may be managed using services such
        as RevenueCat and platform billing APIs. We rely on entitlement data
        (for example, product identifiers, trial eligibility, renewal and
        expiration status) to determine whether you have access to premium
        features such as unlimited storage, additional albums, or biometric
        unlock. An internet connection may be required to validate or refresh
        entitlements after long offline periods. If platform billing reverses,
        refunds, or flags a transaction as fraudulent, related entitlements may
        be limited, suspended, or revoked.
      </p>

      <div className={s.title}>9) Biometric Authentication and PIN</div>
      <p>
        The App may allow you to unlock the vault using biometric authentication
        (such as Face ID or Touch ID) in addition to a numeric PIN. Biometric
        checks are handled by the operating system and may be subject to device
        configuration and policies. If biometric checks fail repeatedly, the OS
        may require your device passcode and/or your vault PIN. You remain
        responsible for choosing a strong PIN, keeping it secret, and managing
        biometric enrollment on your device (for example, which faces or
        fingerprints are registered).
      </p>
      <p>
        If you share your device, passcode, or biometric access with others,
        they may be able to unlock the vault. We are not responsible for access
        caused by your device settings or sharing.
      </p>

      <div className={s.title}>10) Acceptable Use and Prohibited Content</div>
      <p>
        You are solely responsible for the photos and videos you store in the
        App and for ensuring that your content and use of the App comply with
        applicable laws and platform rules. You must not use the App to:
      </p>
      <ul>
        <li>
          store, share, or manage content that is illegal in your jurisdiction,
          including content involving exploitation or abuse;
        </li>
        <li>
          infringe intellectual property rights, privacy rights, or other rights
          of any person;
        </li>
        <li>
          hide content for the purpose of committing fraud or evading lawful
          investigations;
        </li>
        <li>
          reverse engineer, decompile, or attempt to extract the App&apos;s
          source code except where permitted by law;
        </li>
        <li>
          circumvent technical protections, capacity limits, or paywalls
          separating free and premium features.
        </li>
      </ul>
      <p>
        If we reasonably believe your use violates these rules or applicable
        law, we may restrict, suspend, or terminate access and may cooperate
        with platform operators or authorities where legally required.
      </p>

      <div className={s.title}>11) Intellectual Property</div>
      <p>
        You retain ownership of your photos, videos, and other content you store
        in the App, subject to rights of third parties. All software, user
        interface designs, icons, logos, and other materials provided with the
        App (excluding your content) are owned by the publisher or its licensors
        and are protected by intellectual property laws. Subject to these Terms
        and any Subscription limitations, you are granted a limited,
        non-exclusive, non-transferable, revocable license to use the App and
        its features solely for personal vault and organization purposes.
      </p>

      <div className={s.title}>12) Analytics and Performance Monitoring</div>
      <p>
        We may use analytics and crash-reporting tools (for example, Firebase
        Analytics or similar services) to understand how the App is used,
        improve reliability, and diagnose technical issues. Analytics typically
        focuses on technical and usage data (such as screen views, button taps,
        paywall views, and purchase events) rather than the actual media you
        store. Where required by law, we will obtain necessary permissions or
        provide options to manage analytics preferences.
      </p>

      <div className={s.title}>13) No Warranties; Service Changes</div>
      <p>
        To the maximum extent permitted by law, the App is provided “as is” and
        “as available” without warranties of any kind, whether express or
        implied, including but not limited to implied warranties of
        merchantability, fitness for a particular purpose, and
        non-infringement. We do not warrant that:
      </p>
      <ul>
        <li>the App will always securely store your content without loss;</li>
        <li>
          the App will be free from errors, vulnerabilities, or interruptions;
        </li>
        <li>
          the App will prevent all unauthorized access arising from device-level
          issues, malware, or user behavior.
        </li>
      </ul>
      <p>
        Features, limits, and design of the App may change over time as we
        update, improve, or simplify the product.
      </p>

      <div className={s.title}>14) Limitation of Liability</div>
      <p>
        To the extent allowed by law, we are not liable for any indirect,
        incidental, consequential, or punitive damages, loss of profits, loss of
        data (including loss of any photos, videos, or albums stored in the
        vault), or business interruption arising from or related to your use of
        or inability to use the App. Our total aggregate liability to you for
        direct damages arising out of or relating to the App will be limited to
        the amount you paid for Subscriptions during the twelve (12) months
        prior to the claim, or the minimum amount permitted by applicable law if
        greater. Some jurisdictions do not allow certain limitations or
        exclusions, so parts of this section may not apply to you.
      </p>

      <div className={s.title}>15) Indemnification</div>
      <p>
        To the extent permitted by law, you agree to indemnify and hold harmless
        the publisher and its affiliates from any claims, liabilities, damages,
        losses, and expenses (including reasonable legal fees) arising out of or
        related to your misuse of the App, your content, your breach of these
        Terms, or your violation of any rights of a third party.
      </p>

      <div className={s.title}>16) Termination</div>
      <p>
        We may suspend or terminate your access to the App or to specific
        features at any time if we reasonably believe you have violated these
        Terms, misused the App, or where required by law, platform rules, or
        security concerns. You may stop using the App at any time by
        uninstalling it and, where applicable, cancelling your Subscription via
        platform settings. Sections that by their nature should survive
        termination (for example, intellectual property, acceptable use,
        limitation of liability, and indemnification) will continue to apply
        after your access ends.
      </p>

      <div className={s.title}>17) Privacy and Data Protection</div>
      <p>
        Your use of the App is also governed by the separate Privacy Policy for
        Veilify Photo Vault, which explains how information is collected, used,
        stored, and shared, including technical and analytics data. In case of
        conflict between these Terms and the Privacy Policy regarding data
        processing, the Privacy Policy will guide how we handle personal data,
        while these Terms govern your use of the App and its features. You
        should review the Privacy Policy regularly to understand current
        practices.
      </p>

      <div className={s.title}>18) Changes to These Terms</div>
      <p>
        We may update the App, add or remove features (including changes to free
        and premium offerings), or modify these Terms from time to time. The
        “Last updated” date at the top of this document indicates when these
        Terms were last revised. Material changes may be highlighted in the App
        or via other reasonable means. By continuing to use the App after
        changes take effect, you accept the updated Terms. If you do not agree
        to the revised Terms, you must stop using the App and uninstall it.
      </p>

      <div className={s.title}>19) Contact Information</div>
      <p>
        If you have questions about these Terms or the App, you can contact us
        at: <strong>alexwadek@gmail.com</strong>. We may require reasonable
        information to verify your identity before discussing account-specific
        or purchase-related matters.
      </p>
    </TermOfUseLayout>
  );
}
